import React, { useState, useEffect, useRef } from 'react';
import { Mail, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FormInput = ({ label, error, ...props }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-white">
      {label}
    </label>
    <input
      {...props}
      className={`
        flex h-10 w-full rounded-md border px-3 py-2 text-sm transition-all duration-200
        file:border-0 file:bg-transparent file:text-sm file:font-medium
        placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2
        disabled:cursor-not-allowed disabled:opacity-50
        bg-black/30 border-coral-900/30
        text-white
        ${error ? 'border-red-500 focus-visible:ring-red-500' : 'focus-visible:ring-coral-500'}
      `}
    />
    {error && (
      <motion.p
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-sm text-red-400"
      >
        {error}
      </motion.p>
    )}
  </div>
);

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const honeypotRef = useRef(null);
  const formLoadTime = useRef(Date.now());
  const [letterCount, setLetterCount] = useState(0);

  useEffect(() => {
    const honeypot = document.createElement('input');
    honeypot.type = 'text';
    honeypot.className = 'absolute opacity-0 h-0 w-0 -z-10';
    honeypot.name = 'website';
    honeypot.tabIndex = -1;
    honeypot.autocomplete = 'off';
    if (honeypotRef.current) {
      honeypotRef.current.appendChild(honeypot);
    }

    return () => {
      if (honeypotRef.current?.firstChild) {
        honeypotRef.current.removeChild(honeypotRef.current.firstChild);
      }
    };
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.length > 5000) {
      errors.message = 'Message is too long (max 5000 characters)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Update letter count for message field
    if (name === 'message') {
      setLetterCount(value.length);
    }
    
    // Clear submit status when form is modified
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Honeypot check
    if (honeypotRef.current?.firstChild?.value) {
      setSubmitStatus('error');
      return;
    }

    // Anti-spam timing check
    if (Date.now() - formLoadTime.current < 2000) {
      setFormErrors({
        message: 'Please take a moment to review your message before sending'
      });
      return;
    }

    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const submission = {
        ...formData,
        timestamp: formLoadTime.current
      };

      const response = await fetch('https://api.developir.ai/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submission)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      });
      setLetterCount(0);
      formLoadTime.current = Date.now();

    } catch (err) {
      setSubmitStatus('error');
      setFormErrors({
        submit: err.message || 'An error occurred while sending your message'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-16 px-4 bg-brand-dark text-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 font-serif">
            Contact Us
          </h2>
          <p className="text-xl max-w-3xl mx-auto text-gray-300">
            Ready to transform your business with technology? Let's start a conversation.
          </p>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-lg border border-coral-900/50 max-w-4xl mx-auto bg-black/40 backdrop-blur-sm shadow-lg overflow-hidden"
        >
          <div className="flex flex-col p-6 space-y-1 bg-gradient-to-r from-coral-500/10 to-transparent">
            <motion.div 
              initial={{ x: -20 }}
              animate={{ x: 0 }}
              className="flex items-center gap-2 mb-2"
            >
              <Mail className="h-6 w-6 text-coral-400" />
              <h3 className="tracking-tight text-2xl font-bold text-white font-serif">
                Get in Touch
              </h3>
            </motion.div>
            <p className="text-gray-300">
              Ready to transform your business with cutting-edge technology? Let's start a conversation about your needs.
            </p>
          </div>

          <div className="p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div ref={honeypotRef} />
              
              <FormInput
                label="Name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                disabled={isLoading}
                error={formErrors.name}
                required
              />

              <FormInput
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email Address"
                disabled={isLoading}
                error={formErrors.email}
                required
              />

              <FormInput
                label="Company (Optional)"
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Company Name"
                disabled={isLoading}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-white">
                  Message
                </label>
                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project or business needs..."
                    required
                    rows={4}
                    disabled={isLoading}
                    className={`
                      w-full px-3 py-2 rounded-md transition-all duration-200
                      bg-black/30 border border-coral-900/30
                      text-white placeholder-gray-400
                      focus:border-coral-500 focus:ring-coral-500 focus-visible:outline-none
                      focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50
                      ${formErrors.message ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    `}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                    {letterCount}/5000
                  </div>
                </div>
                {formErrors.message && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-400"
                  >
                    {formErrors.message}
                  </motion.p>
                )}
              </div>

              <AnimatePresence mode="wait">
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-3 rounded bg-green-900/20 border border-green-800"
                  >
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Message sent successfully! We'll get back to you soon.</span>
                    </div>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-3 rounded bg-red-900/20 border border-red-800"
                  >
                    <div className="flex items-center gap-2 text-red-400">
                      <AlertCircle className="h-4 w-4" />
                      <span>{formErrors.submit || 'An error occurred. Please try again.'}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                type="submit"
                disabled={isLoading}
                className={`
                  inline-flex items-center justify-center rounded-full text-sm font-medium
                  transition-all duration-200 focus-visible:outline-none focus-visible:ring-2
                  focus-visible:ring-coral-500 disabled:pointer-events-none disabled:opacity-50
                  bg-coral-500 text-white hover:bg-coral-600 h-10 px-6 py-2 w-full
                  ${isLoading ? 'cursor-wait' : ''}
                `}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
